import { useEffect, useState, useCallback } from "react";
import { Plus, Search, Eye, Edit2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api, formatApiErrorDetail } from "../lib/api";

const EMPTY = { email: "", password: "", name: "", address: "", city: "", phone: "", lat: 0, lng: 0, image_url: "" };

export default function AdminClinics() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [viewId, setViewId] = useState(null);
  const [viewData, setViewData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "20" });
      if (q) params.set("q", q);
      if (activeOnly) params.set("active", "true");
      const { data } = await api.get(`/admin/clinics?${params.toString()}`);
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  }, [q, activeOnly, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!viewId) return;
    api.get(`/admin/clinics/${viewId}`).then((r) => setViewData(r.data));
  }, [viewId]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY }); setOpenForm(true); };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ email: c.email, password: "", name: c.name, address: c.address, city: c.city, phone: c.phone, lat: c.lat || 0, lng: c.lng || 0, image_url: c.image_url || "" });
    setOpenForm(true);
  };
  const submitForm = async () => {
    try {
      if (editing) {
        const payload = { ...form }; delete payload.email; delete payload.password;
        await api.patch(`/admin/clinics/${editing}`, payload);
        toast.success("Clinic updated");
      } else {
        await api.post("/admin/clinics", form);
        toast.success("Clinic added");
      }
      setOpenForm(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const toggleActive = async (c) => {
    try { await api.patch(`/admin/clinics/${c.id}`, { is_active: !c.is_active }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updNum = (k) => (e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) || 0 }));

  return (
    <AdminLayout title="Clinics" subtitle={`${total} total`} action={
      <Button data-testid="admin-add-clinic-btn" onClick={openCreate} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Add Clinic</Button>
    }>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-clinics-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name/email/city" className="pl-9 rounded-full" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid="admin-clinics-active-only" checked={activeOnly} onCheckedChange={(v) => { setActiveOnly(v); setPage(1); }} />Active only
        </label>
      </div>

      <div data-testid="admin-clinics-table" className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left p-3">Clinic</th><th className="text-left p-3">City</th><th className="text-left p-3">Phone</th>
                <th className="text-center p-3">Active</th><th className="text-right p-3">Actions</th></tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Loading…</td></tr>
                : items.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No clinics</td></tr>
                : items.map((c) => (
                <tr key={c.id} data-testid={`admin-clinic-row-${c.id}`} className="border-t border-border hover:bg-secondary/30">
                  <td className="p-3"><div className="font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.email}</div></td>
                  <td className="p-3">{c.city}</td>
                  <td className="p-3 text-xs">{c.phone}</td>
                  <td className="p-3 text-center"><Switch data-testid={`admin-clinic-toggle-${c.id}`} checked={c.is_active} onCheckedChange={() => toggleActive(c)} /></td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" data-testid={`admin-clinic-view-${c.id}`} onClick={() => setViewId(c.id)}><Eye className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" data-testid={`admin-clinic-edit-${c.id}`} onClick={() => openEdit(c)}><Edit2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent data-testid="admin-clinic-form-modal" className="sm:max-w-xl rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Clinic" : "Add Clinic"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><label className="text-xs font-medium">Email</label>
              <Input data-testid="clinic-form-email" value={form.email} onChange={upd("email")} disabled={!!editing} /></div>
            {!editing && (
              <div className="space-y-1.5"><label className="text-xs font-medium">Password</label>
                <Input data-testid="clinic-form-password" value={form.password} onChange={upd("password")} /></div>
            )}
            <div className="space-y-1.5"><label className="text-xs font-medium">Name</label>
              <Input data-testid="clinic-form-name" value={form.name} onChange={upd("name")} /></div>
            <div className="space-y-1.5"><label className="text-xs font-medium">City</label>
              <Input data-testid="clinic-form-city" value={form.city} onChange={upd("city")} /></div>
            <div className="space-y-1.5 sm:col-span-2"><label className="text-xs font-medium">Address</label>
              <Input data-testid="clinic-form-address" value={form.address} onChange={upd("address")} /></div>
            <div className="space-y-1.5"><label className="text-xs font-medium">Phone</label>
              <Input data-testid="clinic-form-phone" value={form.phone} onChange={upd("phone")} /></div>
            <div className="space-y-1.5"><label className="text-xs font-medium">Image URL</label>
              <Input value={form.image_url} onChange={upd("image_url")} placeholder="https://..." /></div>
            <div className="space-y-1.5"><label className="text-xs font-medium">Lat</label>
              <Input type="number" step="0.0001" value={form.lat} onChange={updNum("lat")} /></div>
            <div className="space-y-1.5"><label className="text-xs font-medium">Lng</label>
              <Input type="number" step="0.0001" value={form.lng} onChange={updNum("lng")} /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenForm(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="admin-clinic-save-btn" onClick={submitForm} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!viewId} onOpenChange={(o) => { if (!o) { setViewId(null); setViewData(null); } }}>
        <SheetContent data-testid="admin-clinic-view-drawer" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle>{viewData?.clinic?.name || "Clinic"}</SheetTitle></SheetHeader>
          {viewData && (
            <div className="space-y-4 mt-4">
              <div className="bg-secondary/40 rounded-xl p-3 text-sm">
                <div className="font-medium">{viewData.clinic.email}</div>
                <div className="text-muted-foreground text-xs">{viewData.clinic.address}, {viewData.clinic.city}</div>
                <div className="text-xs">{viewData.clinic.phone}</div>
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">Doctor roster ({viewData.associations.length})</div>
                <div className="space-y-2">
                  {viewData.associations.map((a) => (
                    <div key={a.id} className="bg-white border border-border rounded-xl p-2 text-sm">
                      <div className="font-medium">{a.doctor?.name}</div>
                      <div className="text-xs text-muted-foreground">{a.doctor?.category}</div>
                    </div>
                  ))}
                  {viewData.associations.length === 0 && <div className="text-xs text-muted-foreground">No associated doctors.</div>}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">Devices ({viewData.devices.length})</div>
                <div className="space-y-2">
                  {viewData.devices.map((dv) => (
                    <div key={dv.id} className="bg-white border border-border rounded-xl p-2 text-sm flex items-center justify-between">
                      <div><div className="font-medium">{dv.name}</div><div className="text-xs text-muted-foreground">{(dv.supported_tests || []).join(", ")}</div></div>
                      <span className={"text-xs px-2 py-0.5 rounded-full " + (dv.status === "available" ? "bg-emerald-50 text-emerald-700" : dv.status === "in_use" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700")}>{dv.status}</span>
                    </div>
                  ))}
                  {viewData.devices.length === 0 && <div className="text-xs text-muted-foreground">No devices.</div>}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
}
